import { AxiosRequestConfig, AxiosResponse } from 'axios';
import apiClient from '../apis/fetch';
import { hitRefreshToken } from '../apis/user';
import { setCookie, deleteCookie } from './cookies';

// Extend AxiosRequestConfig to include our retry flag
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _isRetry?: boolean;
}

/**
 * Enhanced API call function with automatic token refresh and retry on 403 errors
 * @param config - Axios request configuration
 * @returns Promise with the API response
 */
export const apiCallWithRetry = async <T = any>(
  config: ExtendedAxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  try {
    const response = await apiClient.request<T>(config);
    return response;
  } catch (error: any) {
    // If we get a 403 error, try to refresh the token and retry once
    if (error?.response?.status === 403 && !config._isRetry) {
      try {
        console.log('403 error detected, attempting token refresh...');
        const refreshResponse = await hitRefreshToken();
        
        if (refreshResponse?.status === 200) {
          // Update the token in the cookie
          setCookie("accessToken", refreshResponse.data.result.access_token, 1);
          
          // Update the authorization header for this request
          const newConfig: ExtendedAxiosRequestConfig = {
            ...config,
            _isRetry: true, // Mark as retry to prevent infinite loops
            headers: {
              ...config.headers,
              Authorization: `Bearer ${refreshResponse.data.result.access_token}`,
            },
          };
          
          // Update the default apiClient headers as well
          apiClient.defaults.headers.common["Authorization"] = `Bearer ${refreshResponse.data.result.access_token}`;
          
          console.log('Token refreshed successfully, retrying original request...');
          // Retry the original request with the new token
          return await apiClient.request<T>(newConfig);
        } else {
          console.error('Token refresh failed');
          throw error;
        }
      } catch (refreshError: any) {
        console.error('Error during token refresh:', refreshError);
        
        // If refresh fails with 401, clear cookies and redirect
        if (refreshError?.response?.status === 401) {
          deleteCookie("accessToken");
          deleteCookie("userID");
          window.location.href = "/";
        }
        
        throw error; // Throw the original error
      }
    }
    
    throw error;
  }
};

/**
 * Wrapper for download operations with blob response type
 * @param url - The download endpoint URL
 * @param params - Query parameters
 * @param filename - The filename for the downloaded file
 * @returns Promise<void>
 */
export const downloadWithRetry = async (
  url: string,
  params: Record<string, any>,
  filename: string
): Promise<void> => {
  const config: ExtendedAxiosRequestConfig = {
    method: 'GET',
    url,
    params,
    responseType: 'blob',
  };
  
  try {
    const response = await apiCallWithRetry(config);
    
    // Create download link
    const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    // Clean up the URL object
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error(`Error downloading ${filename}:`, error);
    throw error;
  }
};

/**
 * Wrapper for download operations with blob response type using POST
 * @param url - The download endpoint URL
 * @param data - POST body payload
 * @param filename - The filename for the downloaded file
 * @returns Promise<void>
 */
export const downloadPostWithRetry = async (
  url: string,
  data: Record<string, any>,
  filename: string
): Promise<void> => {
  const config: ExtendedAxiosRequestConfig = {
    method: 'POST',
    url,
    data,
    responseType: 'blob',
  };

  try {
    const response = await apiCallWithRetry(config);

    // Create download link
    const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    // Clean up the URL object
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error(`Error downloading ${filename}:`, error);
    throw error;
  }
};






